import { UserApi } from '@/api/user-api'

import { parse, differenceInHours } from 'date-fns'

import { defineStore } from 'pinia'

import { useCommonStore } from '@/stores/common'

import { MAX_HOURS_SIGN_IN, USER_GROUP_TYPE_GESTIONNAIRE } from '@/constants'

import {
  SIGNED_IN_STATUS,
  STATUS_LOAD,
  USER_FILTERS_TYPE,
  KEY_ACTIVE,
  STATUS,
  FEATURES,
  REGEX_EMAIL,
  USER_GROUP_TYPE_PROVIDER,
  USER_GROUP_TYPE_CUSTOMER,
  USER_GROUP_TYPE_ADMINISTRATOR,
} from '@/constants'

export interface ListUser {
  username: string
  // Breaking convention, as the data are supposed to arrive straight from the backend with snake case convention
  create_date: string
  status: string
  active: boolean
  groups: Array<string>
  company: string
  ftpUser: string
  isDeleting: boolean
  isUpdating: boolean
}

interface User {
  username: string
  idToken: string
  accessToken: string
  refreshToken: string
  status: boolean
  company: string
  featuresAccess: Array<string>
}

interface ModalUser {
  username: string
  password: string
  active: boolean
  company: string
  groups: Array<string>
  ftpUser: string
  apiStatus: STATUS
}

interface State {
  user: User
  signInStatus: SIGNED_IN_STATUS
  api: UserApi
  adminOnly: boolean
  users: Array<ListUser>
  statusLoad: STATUS_LOAD
  deleteStatus: STATUS
  filterGroupTypesSelected: Array<string>
  filterStatusActiveSelected: Array<string>
  filterCompanySelected: Array<string>
  filteredUsers: Array<ListUser>
  filterOptionValues: Record<string, any>
  filterOptionsSelected: Array<Record<string, string | boolean>>
  filterCompanies: Array<string>
  limit: number
  offset: number
  indexUserSelected: number
  modalUser: ModalUser
  // If you wonder why we aren't including it in the ModalUser struct...simply because it's relevant to only new user, not updating one, so
  // don't want to polluate ModalUser
  sendConnectionInfo: boolean
}

export const useUserStore = defineStore('user', {
  persist: {
    afterRestore: (context) => {
      const signedIngDate: string = useCommonStore().getSignedInDate

      if (signedIngDate !== '') {
        console.log('User Store afterRestore', signedIngDate)

        const signedInDateConverted = parse(
          signedIngDate,
          "yyyy-MM-dd'T'HH:mm:ss.SSSX",
          new Date()
        )

        const currentDate: Date = new Date()

        const hoursSinceSignedIn = differenceInHours(
          currentDate,
          signedInDateConverted
        )

        // Check if the signedIn is more than 24h ago, if it is, we automatically sign out
        if (hoursSinceSignedIn >= MAX_HOURS_SIGN_IN) {
          context.store.clearUser()
        }
      }

      // Reset modal user,otherwise clicking on New User might think it's still in update
      context.store.resetModalUser()
    },
  },
  // convert to a function
  state: (): State => ({
    user: {
      username: '',
      idToken: '',
      accessToken: '',
      refreshToken: '',
      status: false,
      company: '',
      featuresAccess: [],
    },
    signInStatus: SIGNED_IN_STATUS.IDLE,
    api: new UserApi(),
    adminOnly: false,
    users: [],
    statusLoad: STATUS_LOAD.IDLE,
    deleteStatus: STATUS.IDLE,
    filterGroupTypesSelected: [],
    filterStatusActiveSelected: [],
    filterCompanySelected: [],
    filteredUsers: [],
    filterOptionValues: {},
    filterOptionsSelected: [],
    filterCompanies: [],
    limit: 10,
    offset: 0,
    indexUserSelected: -1,
    modalUser: {
      username: '',
      password: '',
      active: true,
      company: '',
      groups: [],
      ftpUser: '',
      apiStatus: STATUS.IDLE,
    },
    sendConnectionInfo: false,
  }),

  getters: {
    getUser: (state) => {
      return state.user
    },

    getToken: (state) => {
      return state.user.idToken
    },

    isConnected: (state) => {
      return state.user.idToken !== ''
    },

    getEmail: (state) => {
      return state.user.username
    },

    getSignInStatus: (state) => {
      return state.signInStatus
    },

    isAdminOnly: (state) => {
      return state.adminOnly
    },

    isAdmin: (state) => {
      // return true
      console.log('isAdmin', state.user.featuresAccess.includes(FEATURES.ADMIN))
      return state.user.featuresAccess.includes(FEATURES.ADMIN)
    },

    checkFeatureAccess: (state) => {
      return (feature: string) => state.user.featuresAccess.includes(feature)
    },

    checkUsernameExist: (state) => {
      return (username: string) => {
        console.log('checkUsernameExist ', username)

        if (username !== '') {
          return (
            state.users.find((item) => item.username === username) !== undefined
          )
        } else {
          return false
        }
      }
    },

    checkFtpUsernameExist: (state) => {
      console.log('checkFtpUsernameExist ')

      let exist = false

      if (state.modalUser.ftpUser !== '') {
        const user: ListUser | undefined = state.users.find(
          (item) => item.ftpUser === state.modalUser.ftpUser
        )

        // Check that we're updating an user, and if we are, then if the name is the same as the current user
        if (user) {
          exist = user.username !== state.modalUser.username
        }
      }

      return exist
    },

    getUsers: (state) => {
      return state.users
    },

    getFilteredUsers: (state) => {
      return state.filteredUsers
    },

    getStatusLoad: (state) => {
      return state.statusLoad
    },

    getDeleteStatus: (state) => {
      return state.deleteStatus
    },

    getFilterOptionsSelected: (state) => {
      return state.filterOptionsSelected
    },

    getFilterGroupTypesSelected: (state) => {
      return state.filterGroupTypesSelected
    },

    getFilterActiveStatusSelected: (state) => {
      return state.filterStatusActiveSelected
    },

    getFilterCompanySelected: (state) => {
      return state.filterCompanySelected
    },

    getFilterCompanies: (state) => {
      return state.filterCompanies
    },

    isMorePreviousAvailable: (state) => {
      return state.offset > 0
    },

    isMoreNextAvailable: (state) => {
      return state.offset + state.limit < state.filteredUsers.length
    },

    getUserSelected: (state) => {
      if (
        state.indexUserSelected >= 0 &&
        state.indexUserSelected < state.filteredUsers.length
      ) {
        return state.filteredUsers[state.indexUserSelected]
      } else {
        return null
      }
    },

    getUserModal: (state) => {
      return state.modalUser
    },

    getFtpGroupPresentModalUser: (state) => {
      return (
        state.modalUser.groups.includes(USER_GROUP_TYPE_ADMINISTRATOR) ||
        state.modalUser.groups.includes(USER_GROUP_TYPE_PROVIDER)
      )
    },

    getFtpUserNameValid: (state) => {
      const ftpGroupPresent =
        state.modalUser.groups.includes(USER_GROUP_TYPE_ADMINISTRATOR) ||
        state.modalUser.groups.includes(USER_GROUP_TYPE_PROVIDER)
      console.log('getFtpUserNameVali ftpGroupPresentd', ftpGroupPresent)

      const isValid =
        (ftpGroupPresent && state.modalUser.ftpUser !== '') ||
        (!ftpGroupPresent &&
          (state.modalUser.groups.includes(USER_GROUP_TYPE_CUSTOMER) ||
            state.modalUser.groups.includes(USER_GROUP_TYPE_GESTIONNAIRE)))

      console.log('getFtpUserNameValid isValid', isValid)
      return isValid
    },

    isModalUserValid: (state) => {
      console.log('isModalUserValid', state.modalUser)

      const ftpGroupPresent =
        state.modalUser.groups.includes(USER_GROUP_TYPE_ADMINISTRATOR) ||
        state.modalUser.groups.includes(USER_GROUP_TYPE_PROVIDER)

      console.log('isModalUserValid ftpGroupPresent', ftpGroupPresent)

      return (passwordNeeded = true) => {
        console.log('isModalUserValid passwordNeeded', passwordNeeded)

        let is_valid = false
        if (passwordNeeded) {
          is_valid =
            state.modalUser.username.length > 0 &&
            state.modalUser.password.length > 6 &&
            state.modalUser.company !== '' &&
            (ftpGroupPresent ||
              (!ftpGroupPresent &&
                (state.modalUser.groups.includes(USER_GROUP_TYPE_CUSTOMER) ||
                  state.modalUser.groups.includes(
                    USER_GROUP_TYPE_GESTIONNAIRE
                  ))))
        } else {
          is_valid =
            state.modalUser.username.length > 0 &&
            (state.modalUser.password.length === 0 ||
              state.modalUser.password.length > 6) &&
            state.modalUser.company !== '' &&
            (ftpGroupPresent ||
              (!ftpGroupPresent &&
                (state.modalUser.groups.includes(USER_GROUP_TYPE_CUSTOMER) ||
                  state.modalUser.groups.includes(
                    USER_GROUP_TYPE_GESTIONNAIRE
                  ))))
        }

        console.log('isModalUserValid is_valid', is_valid)

        return is_valid
      }
    },
  },

  actions: {
    addFilterOnName(name: string) {
      console.log('addFilterOnName', name)

      if (
        this.filterOptionsSelected.find(
          (item) => item.type === USER_FILTERS_TYPE.NAME
        )
      ) {
        this.filterOptionsSelected = this.filterOptionsSelected.filter(
          (item) => item.type !== USER_FILTERS_TYPE.NAME
        )
      }

      this.filterOptionsSelected.push({
        type: USER_FILTERS_TYPE.NAME,
        value: name,
        text: name,
      })

      this.filteredUsers = this.users.filter((item) =>
        this.checkUserAgainstFilterOptions(item)
      )
    },

    removeFilterOnName() {
      console.log('removeFilterOnName')

      this.filterOptionsSelected = this.filterOptionsSelected.filter(
        (item) => item.type !== USER_FILTERS_TYPE.NAME
      )

      this.filteredUsers = this.users.filter((item) =>
        this.checkUserAgainstFilterOptions(item)
      )
    },

    addFilterGroupType(type: string, text: string) {
      console.log('addFilterGroupType', type, text)

      this.filterGroupTypesSelected.push(text)

      const item = {
        type: USER_FILTERS_TYPE.GROUP,
        value: type,
        text: text,
      }

      this.filterOptionsSelected.push(item)

      this.filteredUsers = this.users.filter((item) =>
        this.checkUserAgainstFilterOptions(item)
      )
    },

    removeFilterGroupType(type: string, text: string) {
      console.log('removeFilterGroupType', type, text)

      this.filterGroupTypesSelected = this.filterGroupTypesSelected.filter(
        (item) => item !== text
      )
      this.filterOptionsSelected = this.filterOptionsSelected.filter(
        (item) => item.text !== text
      )

      this.filteredUsers = this.users.filter((item) =>
        this.checkUserAgainstFilterOptions(item)
      )
    },

    addFilterActiveStatus(status: boolean, text: string) {
      console.log('addFilterActiveStatus', status, text)

      const item = {
        type: USER_FILTERS_TYPE.STATUS_ACTIVE,
        value: status,
        text: text,
      }
      this.filterOptionsSelected.push(item)
      this.filterStatusActiveSelected.push(text)

      console.log(
        'addFilterActiveStatus filterOptionsSelected',
        this.filterOptionsSelected
      )

      this.filteredUsers = this.users.filter((item) =>
        this.checkUserAgainstFilterOptions(item)
      )
    },

    removeFilterActiveStatus(status: boolean, text: string) {
      console.log('removeFilterActiveStatus', status, text)

      this.filterStatusActiveSelected = this.filterStatusActiveSelected.filter(
        (item) => item !== text
      )
      this.filterOptionsSelected = this.filterOptionsSelected.filter(
        (item) => item.text !== text
      )

      this.filteredUsers = this.users.filter((item) =>
        this.checkUserAgainstFilterOptions(item)
      )
    },

    addFilterCompany(text: string) {
      console.log('addFilterCompany', text)

      const item = {
        type: USER_FILTERS_TYPE.COMPANY,
        value: text,
        text: text,
      }
      this.filterOptionsSelected.push(item)
      this.filterCompanySelected.push(text)

      this.filteredUsers = this.users.filter((item) =>
        this.checkUserAgainstFilterOptions(item)
      )
    },

    removeFilterCompany(text: string) {
      console.log('removeFilterCompany', text)

      this.filterCompanySelected = this.filterStatusActiveSelected.filter(
        (item) => item !== text
      )
      this.filterOptionsSelected = this.filterOptionsSelected.filter(
        (item) => item.text !== text
      )

      this.filteredUsers = this.users.filter((item) =>
        this.checkUserAgainstFilterOptions(item)
      )
    },

    removeFilterOptionsSelected(item: Record<string, string | boolean>) {
      console.log('removeFilterOptionsSelected', item)

      if (item.type === USER_FILTERS_TYPE.GROUP) {
        this.removeFilterGroupType(item.value as string, item.text as string)
      } else if (item.type === USER_FILTERS_TYPE.COMPANY) {
        this.removeFilterCompany(item.text as string)
      } else {
        this.removeFilterActiveStatus(
          item.value as boolean,
          item.text as string
        )
      }
    },

    checkUserAgainstFilterOptions(user: ListUser): boolean {
      // Group check
      const filterGroupsActive = this.filterOptionsSelected.filter(
        (item) => item.type === USER_FILTERS_TYPE.GROUP
      )

      console.log(
        'checkUserAgainstFilterOptions filterGroupsActive',
        filterGroupsActive
      )

      let shouldUserBeIncludeFromGroups = true
      if (filterGroupsActive.length > 0) {
        shouldUserBeIncludeFromGroups =
          filterGroupsActive.find((item) =>
            user.groups.includes(item.value as string)
          ) !== undefined
      }

      // Company check
      const filterCompanyActive = this.filterOptionsSelected.filter(
        (item) => item.type === USER_FILTERS_TYPE.COMPANY
      )

      let shouldUserBeIncludeFromCompany = true
      if (filterCompanyActive.length > 0) {
        console.log(
          'checkUserAgainstFilterOptions filterCompanyActive',
          filterCompanyActive
        )

        shouldUserBeIncludeFromCompany =
          filterCompanyActive.find((item) => item.value === user.company) !==
          undefined
      }

      // Status check
      const filtersStatusActive = this.filterOptionsSelected.filter(
        (item) => item.type === USER_FILTERS_TYPE.STATUS_ACTIVE
      )

      let shouldUserBeIncludeFromStatus = true
      if (filtersStatusActive.length > 0) {
        console.log(
          'checkUserAgainstFilterOptions filtersStatusActive',
          filtersStatusActive
        )

        shouldUserBeIncludeFromStatus =
          filtersStatusActive.find((item) => item.value === user.active) !==
          undefined
      }

      // Name Check
      const filterNameActive = this.filterOptionsSelected.find(
        (item) => item.type === USER_FILTERS_TYPE.NAME
      )

      let shouldUserBeIncludeFromName = true
      if (filterNameActive) {
        console.log(
          'checkUserAgainstFilterOptions filterNameActive',
          filterNameActive
        )

        shouldUserBeIncludeFromName = user.username
          .toLocaleLowerCase()
          .includes(filterNameActive.text as string)
      }

      return (
        shouldUserBeIncludeFromStatus &&
        shouldUserBeIncludeFromCompany &&
        shouldUserBeIncludeFromGroups &&
        shouldUserBeIncludeFromName
      )
    },

    setSendConnectionInfo(value: boolean) {
      this.sendConnectionInfo = value
    },

    // no context as first argument, use `this` instead
    async signIn(username: string, password: string) {
      console.log('signIn', username, password)

      this.signInStatus = SIGNED_IN_STATUS.RUNNING

      try {
        const response: any = await this.api.signIn(username, password)
        console.log('signIn', response)

        this.user.username = username
        this.populateUser(response.data)

        console.log('signIn POPULATE')

        this.signInStatus = SIGNED_IN_STATUS.SIGNED_IN
      } catch (error) {
        console.error(error)

        console.log('signIn FAILED')

        this.signInStatus = SIGNED_IN_STATUS.FAILED
      }
    },

    // no context as first argument, use `this` instead
    async signInAdmin(username: string, password: string) {
      console.log('signInAdmin', username, password)

      this.signInStatus = SIGNED_IN_STATUS.RUNNING

      try {
        const response: any = await this.api.signInAdmin(username, password)
        console.log('signInAdmin', response)

        this.populateUser(response.data)

        this.user.username = username
        this.user.featuresAccess.push(FEATURES.ADMIN)

        this.signInStatus = SIGNED_IN_STATUS.SIGNED_IN
      } catch (error) {
        console.error(error)

        this.signInStatus = SIGNED_IN_STATUS.FAILED
      }
    },

    async getListUsers() {
      console.log('getListUsers')

      this.statusLoad = STATUS_LOAD.LOADING

      try {
        this.users = []
        this.filteredUsers = []

        const response: any = await this.api.getUsers()
        console.log('getListUsers', response)

        this.users = response.data
        this.filteredUsers = response.data

        this.filterCompanies = this.users
          .map((item) => item.company)
          .filter((item) => item !== '' && item !== undefined)

        this.statusLoad = STATUS_LOAD.LOADED
      } catch (error) {
        console.error(error)

        this.statusLoad = STATUS_LOAD.FAILED
      }
    },

    // no context as first argument, use `this` instead
    async create() {
      console.log('create', this.modalUser)

      this.modalUser.apiStatus = STATUS.RUNNING

      const request_ftp_user: boolean =
        this.modalUser.groups.includes(USER_GROUP_TYPE_ADMINISTRATOR) ||
        this.modalUser.groups.includes(USER_GROUP_TYPE_PROVIDER)

      try {
        const parameters = {
          username: this.modalUser.username.toLowerCase(),
          password: this.modalUser.password,
          active: this.modalUser.active,
          company: this.modalUser.company,
          groups: this.modalUser.groups,
          ftp_user: request_ftp_user ? this.modalUser.ftpUser : null,
          send_connection_info: this.sendConnectionInfo,
        }

        const response: any = await this.api.create(parameters)
        console.log('create', response)

        this.users.push({
          username: response.data.username,
          create_date: response.data.create_date,
          status: response.data.status,
          active: response.data.active,
          groups: response.data.groups,
          company: response.data.company,
          ftpUser: response.data.ftp_user,
          isDeleting: false,
          isUpdating: false,
        })

        if (!this.filterCompanies.includes(this.modalUser.company as string)) {
          this.filterCompanies.push(this.modalUser.company as string)
        }

        this.filteredUsers = this.users.filter((item) =>
          this.checkUserAgainstFilterOptions(item)
        )

        this.modalUser.apiStatus = STATUS.SUCCESS
      } catch (error) {
        console.error(error)

        this.modalUser.apiStatus = STATUS.FAILED
      }
    },

    // no context as first argument, use `this` instead
    async update() {
      console.log('update', this.modalUser)

      const payload: Record<string, any> = {
        username: this.modalUser.username,
      }

      // Active
      if (
        (this.getUserSelected &&
          this.modalUser.active !== this.getUserSelected.active) ||
        !this.getUserSelected
      ) {
        payload.active = this.modalUser.active
      }

      // Company
      if (
        (this.getUserSelected &&
          this.modalUser.company !== this.getUserSelected.company) ||
        !this.getUserSelected
      ) {
        payload.company = this.modalUser.company
      }

      // Ftp User
      if (
        (this.getUserSelected &&
          this.modalUser.ftpUser !== this.getUserSelected.ftpUser &&
          this.modalUser.ftpUser !== '') ||
        !this.getUserSelected
      ) {
        payload.ftpUser = this.modalUser.ftpUser
      }

      // Password
      if (this.modalUser.password !== '') {
        payload.password = this.modalUser.password
      }

      // Groups
      const unsharedGroupsCount: number =
        this.modalUser.groups.length > this.getUserSelected!.groups.length
          ? this.modalUser.groups.filter(
              (item) => !this.getUserSelected?.groups.includes(item)
            ).length
          : this.getUserSelected!.groups.filter(
              (item) => !this.modalUser.groups.includes(item)
            ).length
      console.log('update unsharedGroupsCount', unsharedGroupsCount)

      if (unsharedGroupsCount > 0) {
        payload.old_groups = this.getUserSelected!.groups.filter(
          (item) => !this.modalUser.groups.includes(item)
        )
        payload.new_groups = this.modalUser.groups.filter(
          (item) => !this.getUserSelected?.groups.includes(item)
        )
      }

      const modalUserUsername: string = this.modalUser.username

      try {
        this.modalUser.apiStatus = STATUS.RUNNING
        this.getUserSelected!.isUpdating = true

        const response: any = await this.api.update(payload)
        console.log('update', response)

        const index: number = this.users.findIndex(
          (item) => item.username === modalUserUsername
        )
        const user: ListUser = this.users.find(
          (item) => item.username === modalUserUsername
        ) as ListUser
        this.users = this.users.filter(
          (item) => item.username !== user.username
        )

        if (payload.ftpUser) {
          user.ftpUser = payload.ftpUser
        }

        if (payload.company) {
          user.company = payload.company
        }

        if (KEY_ACTIVE in payload) {
          user.active = payload.active
        }

        if (unsharedGroupsCount > 0) {
          user.groups = this.modalUser.groups
        }

        this.users.splice(index, 0, user)
        this.filteredUsers = this.users.filter((item) =>
          this.checkUserAgainstFilterOptions(item)
        )

        this.modalUser.apiStatus = STATUS.SUCCESS
      } catch (error) {
        console.error(error)

        this.modalUser.apiStatus = STATUS.FAILED
      } finally {
        const user: ListUser | undefined = this.users.find(
          (item) => item.username === modalUserUsername
        )
        if (user) {
          user.isUpdating = false
        }
      }
    },

    async delete(item: ListUser) {
      console.log('delete', item)

      try {
        this.deleteStatus = STATUS.RUNNING

        item.isDeleting = true

        await this.api.delete(item.username)

        this.users = this.users.filter(
          (element) => element.username !== item.username
        )

        this.filteredUsers = this.users.filter((element) =>
          this.checkUserAgainstFilterOptions(element)
        )

        this.deleteStatus = STATUS.SUCCESS
      } catch (error) {
        console.error(error)

        this.deleteStatus = STATUS.FAILED
      } finally {
        item.isDeleting = false
      }
    },

    populateUser(data: Record<string, any>) {
      console.log('populateUser')

      this.user.idToken = data['id_token']
      this.user.accessToken = data['access_token']
      this.user.refreshToken = data['refresh_token']
      this.user.featuresAccess = data['features']
      this.user.company = data['company']
    },

    setUserSelected(index: number) {
      this.indexUserSelected = index
    },

    // no context as first argument, use `this` instead
    async fakeSetUser() {
      console.log('fakeSetUser')

      this.user.username = 'chris@gmail.com'
      this.user.idToken = 'fdsfdsffsd'
      this.user.accessToken = 'rewrewrew'
      this.user.refreshToken = 'fdsfsew'
      this.user.company = 'Thorge Software'
      this.user.featuresAccess.push(
        FEATURES.ADMIN,
        FEATURES.IPTC,
        FEATURES.CART
      )
    },

    setDeleteStatus(status: STATUS) {
      this.deleteStatus = status
    },

    clearUser() {
      ;(this.user = {
        username: '',
        idToken: '',
        accessToken: '',
        refreshToken: '',
        status: false,
        company: '',
        featuresAccess: [],
      }),
        (this.signInStatus = SIGNED_IN_STATUS.IDLE),
        (this.api = new UserApi()),
        (this.adminOnly = false),
        (this.users = []),
        (this.statusLoad = STATUS_LOAD.IDLE),
        (this.deleteStatus = STATUS.IDLE),
        (this.filterGroupTypesSelected = []),
        (this.filterStatusActiveSelected = []),
        (this.filterCompanySelected = []),
        (this.filteredUsers = []),
        (this.filterOptionValues = {}),
        (this.filterOptionsSelected = []),
        (this.filterCompanies = []),
        (this.limit = 10),
        (this.offset = 0),
        (this.indexUserSelected = -1),
        (this.modalUser = {
          username: '',
          password: '',
          active: true,
          company: '',
          groups: [],
          ftpUser: '',
          apiStatus: STATUS.IDLE,
        })
    },

    setModalUserFromSelectedUser() {
      console.log('setModalUserFromSelectedUser', this.getUserSelected)

      if (this.getUserSelected) {
        this.modalUser.username = this.getUserSelected.username
        this.modalUser.password = ''
        this.modalUser.active = this.getUserSelected.active
        this.modalUser.company = this.getUserSelected.company
        this.modalUser.groups = this.getUserSelected.groups.map((item) => item)
        this.modalUser.ftpUser = this.getUserSelected.ftpUser
        this.modalUser.apiStatus = STATUS.IDLE
      }
    },

    resetModalUser() {
      this.modalUser.username = ''
      this.modalUser.password = ''
      this.modalUser.active = true
      this.modalUser.company = ''
      this.modalUser.groups = []
      this.modalUser.ftpUser = ''
      this.modalUser.apiStatus = STATUS.IDLE
    },
  },
})
